import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreditsPoolStates } from 'src/hooks/useCreditsPoolStates';
import useSubscriptionPlan from 'src/hooks/useSubscriptionPlan';
import { selectIsPaygLicenseActive } from 'src/redux/slices/payg';
import { useSelector } from 'src/redux/store';
import {
  ACCOUNT_TYPE,
  DISCLAIMER_ACTION_KIND,
  LICENSE_STATE
} from 'src/types/enum';
import { NAVIGATIONS } from 'src/utils/constants';
import { getDisclaimerConfig } from 'src/utils/license-disclaimer';

export interface LicenseDisclaimerAction {
  kind: DISCLAIMER_ACTION_KIND;
  label: string;
  email?: string;
  onClick: () => void;
}

export interface LicenseDisclaimer {
  message: string;
  action: LicenseDisclaimerAction | null;
  isWarning: boolean;
  dismissible: boolean;
}

export const useLicenseDisclaimer = (): LicenseDisclaimer | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { isPaygLicenseApplicable } = useSubscriptionPlan();
  const isPaygActive = useSelector(selectIsPaygLicenseActive);
  const { plan, payg } = useCreditsPoolStates();

  const tier: LICENSE_STATE = !isPaygLicenseApplicable
    ? LICENSE_STATE.STANDARD
    : isPaygActive
    ? LICENSE_STATE.PAYG_ACTIVE
    : LICENSE_STATE.PAYG_AVAILABLE;

  const config = getDisclaimerConfig({
    tier,
    plan,
    payg,
    isOwner: user?.accountType === ACCOUNT_TYPE.ORGANIZATION_OWNER
  });

  if (!config) {
    return null;
  }

  return {
    message: t(config.messageKey),
    action: config.action
      ? {
          kind: config.action.kind,
          label: t(config.action.labelKey),
          ...(config.action.kind === DISCLAIMER_ACTION_KIND.CONTACT_SUPPORT && {
            email: user?.supportEmail
          }),
          onClick: () => {
            if (config.action.kind === DISCLAIMER_ACTION_KIND.CONTACT_SUPPORT) {
              window.location.href = `mailto:${user?.supportEmail ?? ''}`;
              return;
            }
            navigate(NAVIGATIONS.SETTINGS_PAY_AS_YOU_GO);
          }
        }
      : null,
    isWarning: config.isWarning,
    dismissible: config.dismissible
  };
};
